import { type IAttribute, type ISectionSettings } from '../store/slices/SurveyTemplateSlice'
import { type DataObject, type DataObjectList, type ICreatedBy, type IModifiedBy, type IOwnedBy } from './DataObject'
import { type SurveyQuestionType } from './Survey'

export interface IOption {
  option: string
  weight: number | null
}

export enum SurveyItemQuestionType {
  FREE_RESPONSE = 'FreeResponse',
  LINEAR_SCALE = 'LinearScale',
  MULTIPLE_CHOICE = 'MultipleChoice',
  DESCRIPTIVE_TEXT = 'DescriptiveText',
  TITLE = 'Title',
}

export interface SurveyInstructorDetails {
  id: string
  title: string
}

export interface SectionBranchingFilters {
  answer: null | string[]
  itemId: string
  itemType: string
  operation: string
  prompt: string
}

export interface ISurveyItem extends ICreatedBy, IOwnedBy, IModifiedBy {
  archivedOn: string | null
  hashedPrompt: string | null
  isArchived: boolean
  isRequired: boolean
  itemId: string
  itemType: SurveyQuestionType
  nextRevision: string | null
  object: IOption[] | string[] | null
  prevRevision: string | null
  prompt: string
  revision: number
  branchingFilters: any
  // CE-193: previously submitted response returned on GET so reopened surveys
  // can hydrate prior answers. Absent/null for surveys that were never submitted.
  answer?: string | string[] | null
}

export interface ISurveySection extends ICreatedBy, IOwnedBy, IModifiedBy {
  archivedOn: string | null
  descr: string | null
  isArchived: boolean
  name: string
  nextRevision: string | null
  prevRevision: string | null
  questionCount: number
  revision: number
  surveyItems: ISurveyItem[]
  surveySectionId: string
  title: string | null
  isRepeatPerInstructor: boolean
  instructorName: string
  instructorId: string
  attributes: IAttribute[]
  branchingFilters: any
  parentSurveySectionId?: string
  surveySectionRevision?: number
  indentation: number
  isSelectInstructorEnabled?: boolean
  settings?: ISectionSettings[]
  isSurveyProjectAddedSection?: boolean
  categoryName?: string

}

export interface ISurveyTemplate extends ICreatedBy, IOwnedBy, IModifiedBy {
  /**
   * Sandbox mode on the owning Survey Project (CR-341). Absent → treated as false.
   *
   * Not sent on this payload yet. `SurveyFeature` badges the survey a respondent is answering
   * from it, and that badge is the only thing telling them their answers are a rehearsal — so it
   * is declared here rather than left to read `undefined` off an `any`, where nothing would have
   * caught it. Until the backend sends it the badge does not render. Found by review, 2026-09-21.
   */
  isSandbox?: boolean

  archivedOn: string | null
  descr: string | null
  isArchived: boolean
  nextRevision: string | null
  prevRevision: string | null
  questionCount: number
  revision: number
  sectionCount: number
  surveySections: ISurveySection[]
  templateId: string
  distributionSeriesTitle: string
  sectionAbbreviation: string
  instructors: SurveyInstructorDetails[]
  title: string

}

export type ISurveyTemplateRow = ISurveyTemplate

export type ISurveyTemplateList = DataObject<ISurveyTemplate>

export type ISurveyTemplateRowDataType = ISurveyTemplateList[]

export type ISurveyTemplateListResponse = DataObjectList<ISurveyTemplate>
